import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useAuth } from '../contexts/AuthContext';
import { updateActivationCode, checkActivation } from '../services/api';

interface InactiveUserMessageProps {
  type: 'qr' | 'event' | 'stamp' | 'history';
}

const InactiveUserMessage: React.FC<InactiveUserMessageProps> = ({ type }) => {
  const { token, setIsActive, refreshTokenFunction } = useAuth();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [codeValidated, setCodeValidated] = useState(false);

  const getTitle = () => {
    switch (type) {
      case 'qr':
        return 'QRコードは利用できません';
      case 'event':
      case 'stamp':
      case 'history':
        return 'アクセス制限';
      default:
        return 'アクセス制限';
    }
  };

  const handleValidateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('アクティベーションコードを入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await checkActivation(code.trim());
      if (!response.data.valid) {
        setError('シリアルコードが有効ではありません');
      } else if (response.data.used) {
        setError('このコードはすでに使われています');
      } else {
        setCodeValidated(true);
      }
    } catch (err: any) {
      setError('コードの検証に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('認証エラー。再度ログインしてください。');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await updateActivationCode(code.trim(), token);
      
      if (response.data.is_active) {
        setSuccess(true);
        setIsActive(true);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setError('アクティベーションに失敗しました');
      }
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        try {
          const newToken = await refreshTokenFunction();
          if (newToken) {
            const retryResponse = await updateActivationCode(code.trim(), newToken);
            if (retryResponse.data.is_active) {
              setSuccess(true);
              setIsActive(true);
              setTimeout(() => {
                window.location.reload();
              }, 1500);
              return;
            }
          }
        } catch (refreshError) {
          // リフレッシュ失敗
        }
      }
      
      if (err.response?.data?.code) {
        setError(Array.isArray(err.response.data.code) 
          ? err.response.data.code[0] 
          : err.response.data.code);
      } else {
        setError('エラーが発生しました。もう一度お試しください。');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', pt: { xs: 7, sm: 8 }, pb: 2, px: 2 }}>
      <Paper sx={{ p: 3, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
        <LockIcon sx={{ fontSize: 60, color: '#d32f2f', mb: 2 }} />
        
        <Typography variant="h6" sx={{ mb: 2, color: '#d32f2f', fontWeight: 700 }}>
          {getTitle()}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 1 }}>
          新制度への移行が必要です。
        </Typography>
        
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          アクティベーションコードを購入して入力してください。
        </Typography>

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            アクティベーションが完了しました！ページを更新します...
          </Alert>
        ) : !codeValidated ? (
          <Box component="form" onSubmit={handleValidateCode} sx={{ mt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              購入したシリアルコードを入力してください。コードはライブ物販でのみ購入できます。
            </Typography>

            <TextField
              fullWidth
              label="アクティベーションコード"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
              disabled={loading}
              autoComplete="off"
            />
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.5,
                fontWeight: 700,
                fontSize: 16,
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
              }}
            >
              {loading ? 'コード確認中...' : 'コード確認'}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleActivate} sx={{ mt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              コード「{code}」でアクティベーションします。
            </Typography>
            
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                py: 1.5,
                fontWeight: 700,
                fontSize: 16,
                background: 'linear-gradient(90deg, #43a047 0%, #66bb6a 100%)',
                mb: 2,
              }}
            >
              {loading ? 'アクティベーション中...' : 'アクティベート'}
            </Button>

            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setCodeValidated(false);
                setCode('');
                setError('');
              }}
              disabled={loading}
            >
              キャンセル
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default InactiveUserMessage;
